import { gql } from '@apollo/client';
import { v4 as uuidv4 } from 'uuid';
import appSyncClient from './appSyncClient';
import { AIErrorType } from '../constants';

// GraphQL Mutation: Send Chat Message
// New backend schema with conversationId and ChatMessageInput payload
const SEND_CHAT_MESSAGE_MUTATION = gql`
  mutation SendChatMessage($conversationId: ID!, $payload: ChatMessageInput!) {
    sendChatMessage(conversationId: $conversationId, payload: $payload) {
      ok
      error
      message
      __typename
    }
  }
`;

// GraphQL Subscription: Real-time Events
const ON_EVENT_UPDATE_SUBSCRIPTION = gql`
  subscription OnEventUpdate($conversationId: ID!) {
    onEventUpdate(conversationId: $conversationId) {
      __typename
      ... on StageEvent {
        correlationId
        sessionId
        message
        timestamp
        stage
        conversationId
      }
      ... on TokenEvent {
        correlationId
        sessionId
        message
        timestamp
        sequence
        isFinal
        conversationId
      }
      ... on ThinkingEvent {
        correlationId
        sessionId
        message
        timestamp
        sequence
        isFinal
        conversationId
      }
      ... on ErrorEvent {
        correlationId
        sessionId
        message
        timestamp
        error
        conversationId
      }
    }
  }
`;

// GraphQL Query: List Conversations
// Returns list of conversations for a user
const LIST_CONVERSATIONS_QUERY = gql`
  query ListConversations($userId: ID!, $limit: Int) {
    listConversations(userId: $userId, limit: $limit) {
      userId
      conversations {
        conversationId
        title
        createdAt
        updatedAt
        userId
      }
      nextToken
    }
  }
`;

// GraphQL Query: Get Messages
// Returns messages for a specific conversation
const GET_MESSAGES_QUERY = gql`
  query GetMessages($conversationId: ID!, $limit: Int, $nextToken: String) {
    getMessages(conversationId: $conversationId, limit: $limit, nextToken: $nextToken) {
      conversationId
      messages {
        createdAt
        entityType
        message
        messageId
        role
        senderId
        sk
      }
      nextToken
    }
  }
`;

// GraphQL Query: Get All Unread Counts
// Returns unread message counts for all conversations of a user
// Uses a dedicated AppSync endpoint separate from the main AI chat API
const GET_ALL_UNREAD_COUNTS_QUERY = gql`
  query GetAllUnreadCounts($userId: ID!, $conversationId: ID) {
    getAllUnreadCounts(userId: $userId, conversationId: $conversationId) {
      conversationId
      unreadCount
    }
  }
`;


// GraphQL Mutation: Mark Conversation as Read
// Resets unread count to 0 when user opens a conversation
const MARK_CONVERSATION_AS_READ_MUTATION = gql`
  mutation MarkConversationAsRead($conversationId: ID!, $lastMessageId: ID!, $userId: ID!) {
    markConversationAsRead(
      conversationId: $conversationId
      lastMessageId: $lastMessageId
      userId: $userId
    ) {
      success
      conversationId
      unreadCount
      error
    }
  }
`;

/**
 * Start AI Chat Session
 * @param {Object} params - Session initialization data
 * @param {string} params.prompt - User's question/message
 * @param {string} params.conversationId - Conversation ID (UUID)
 * @param {string} params.correlationId - Correlation ID for request tracing (UUID)
 * @param {string} params.browserSessionId - Browser session ID for tab isolation (optional)
 * @param {string} params.userEmail - User's email
 * @param {string} params.companyId - Company/tenant ID
 * @param {string} params.userId - User ID
 * @param {string} params.domain - Domain enum: "ZONAR" or "GPST"
 * @returns {Promise<{ok: boolean, conversationId: string}>}
 */
export const startAIChatSession = async (params) => {
  try {
    const {
      prompt,
      conversationId = uuidv4(),
      correlationId = uuidv4(),
      browserSessionId,
      userEmail,
      companyId,
      userId,
      domain = 'ZONAR',
    } = params;

    console.log('🚀 Starting AI session:', { 
      conversationId, 
      correlationId, 
      browserSessionId,
      prompt: prompt.substring(0, 50) + '...' 
    });

    // Build payload object matching backend ChatMessageInput schema
    const payload = {
      prompt,
      userEmail,
      domain,
      companyId,
      userId,
      correlationId,
      // Include sessionId only if provided (for backward compatibility)
      ...(browserSessionId && { sessionId: browserSessionId }),
    };

    const { data, errors } = await appSyncClient.mutate({
      mutation: SEND_CHAT_MESSAGE_MUTATION,
      variables: {
        conversationId,
        payload,
      },
    });

    let errorType = getSendChatError(data, errors);
    if (errorType && errors) {
      console.error('❌ Detected error type', errorType, '. Original mutation errors:', errors);
    }

    // Return data for processing success/error
    return {
      conversationId, // Include for caller convenience
      errorType, // Error type for further processing
    }
  
  } catch (error) {
    console.error('❌ startAIChatSession error:', error);
    throw error;
  }
};

export const getSendChatError = (data, errors) => {
  if (data === null || data?.sendChatMessage === null || data?.sendChatMessage?.ok === false) {
    if (data?.sendChatMessage?.error === AIErrorType.INPUT_VALIDATION_ERROR) {
      return AIErrorType.INPUT_VALIDATION_ERROR;
    }

    if (errors?.length && errors[0]?.locations?.length > 0 && errors[0]?.errorType !== "Lambda:Unhandled") {
      return AIErrorType.SCHEMA_VALIDATION_ERROR;
    }

    return AIErrorType.SERVER_ERROR;
  } else {
    return null;
  }
};

/**
 * Subscribe to AI Events (WebSocket)
 * Uses AppSync's CONTROL_EVENTS to detect when subscription is truly connected
 * @param {string} conversationId - Active conversation ID
 * @param {Function} onEvent - Callback for each event (from this session)
 * @param {Function} onError - Error callback
 * @param {Function} onConnected - Callback when subscription is connected (fires on CONNECTED control message)
 * @param {Object} options - Optional configuration
 * @param {string} options.browserSessionId - Browser session ID for filtering events
 * @param {Function} options.onOtherSessionComplete - Callback when another session completes (for history sync)
 * @returns {ZenObservable.Subscription}
 */
export const subscribeToAIEvents = (conversationId, onEvent, onError, onConnected, options = {}) => {
  const { browserSessionId, onOtherSessionComplete } = options;
  
  // Enable control events to receive CONNECTED message from AppSync
  const observable = appSyncClient.subscribe({
    query: ON_EVENT_UPDATE_SUBSCRIPTION,
    variables: { conversationId },
    context: {
      controlMessages: {
        '@@controlEvents': true, // Enable control events to detect connection
      },
    },
  });

  let hasConnected = false;

  const subscription = observable.subscribe({
    next: ({ data, errors, extensions }) => {
      // Check for CONNECTED control message from AppSync
      // This fires when WebSocket handshake + subscription start_ack is complete
      const controlMsgType = extensions?.controlMsgType;
      if (controlMsgType === 'CONNECTED') {
        console.log('✅ [WebSocket] Received CONNECTED control message - subscription ready');
        if (!hasConnected) {
          hasConnected = true;
          if (onConnected) {
            onConnected();
          }
        }
        return; // Don't process control messages as events
      }
      
      if (errors) {
        console.error('Subscription returned errors:', errors);
      }
      
      if (data?.onEventUpdate) {
        const event = data.onEventUpdate;
        
        // Session-based event filtering (only if browserSessionId provided)
        // This isolates streaming events between different browser tabs
        if (browserSessionId && event.sessionId) {
          const isMySession = event.sessionId === browserSessionId;
          const isCompleteEvent = event.__typename === 'StageEvent' && event.stage === 'COMPLETE';
          
          if (!isMySession) {
            if (isCompleteEvent && onOtherSessionComplete) {
              // Another tab completed - trigger history sync
              console.log('📥 [WebSocket] Other session completed, triggering history sync');
              onOtherSessionComplete(event);
            } else {
              // Streaming event from another session - ignore
              console.log('🔇 [WebSocket] Ignoring event from other session:', event.sessionId);
            }
            return; // Don't process events from other sessions
          }
        }
        
        // Process event normally (this session's events)
        onEvent(event);
      }
    },
    error: (error) => {
      console.error('Subscription error:', error.message);
      if (onError) onError(error);
    },
    complete: () => {
      // Subscription completed
    },
  });

  return subscription;
};

/**
 * List Conversations for a user
 * @param {string} userId - User ID
 * @param {number} limit - Number of conversations to fetch
 * @returns {Promise<Object>} Object with conversations array and nextToken
 */
export const listConversations = async (userId, limit = 10) => {
  try {
    console.log('🌐 [listConversations] Fetching conversations:', { userId, limit });

    const variables = {
      userId,
      limit,
    };

    const { data, errors } = await appSyncClient.query({
      query: LIST_CONVERSATIONS_QUERY,
      variables,
      fetchPolicy: 'network-only',
    });

    if (errors) {
      const errorMessage = errors[0]?.message || 'Failed to list conversations';
      if (errorMessage.includes('FieldUndefined') || errorMessage.includes('listConversations')) {
        console.warn('⚠️ [listConversations] Backend query not implemented yet.');
        return { conversations: [], nextToken: null };
      }
      console.error('❌ [listConversations] GraphQL errors:', errors);
      throw new Error(errorMessage);
    }

    const conversations = data.listConversations?.conversations || [];
    const resultNextToken = data.listConversations?.nextToken || null;

    console.log('✅ [listConversations] Success:', {
      conversationCount: conversations.length,
      hasNextToken: !!resultNextToken,
    });
    
    return {
      conversations,
      nextToken: resultNextToken,
    };
  } catch (error) {
    const errorMessage = error.message || '';
    if (errorMessage.includes('FieldUndefined') || errorMessage.includes('listConversations')) {
      console.warn('⚠️ [listConversations] Backend not ready.');
      return { conversations: [], nextToken: null };
    }
    console.error('❌ [listConversations] Unexpected error:', error);
    return { conversations: [], nextToken: null };
  }
};

/**
 * Get Messages for a conversation
 * @param {string} conversationId - Conversation ID
 * @param {number} limit - Number of messages to fetch
 * @param {string} nextToken - Pagination token
 * @returns {Promise<Object>} Object with messages array and nextToken
 */
export const getMessages = async (conversationId, limit = 50, nextToken = null) => {
  try {
    console.log('🌐 [getMessages] Fetching messages:', { conversationId, limit, nextToken });

    const variables = {
      conversationId,
      limit,
    };

    if (nextToken) {
      variables.nextToken = nextToken;
    }

    const { data, errors } = await appSyncClient.query({
      query: GET_MESSAGES_QUERY,
      variables,
      fetchPolicy: 'network-only',
    });

    if (errors) {
      const errorMessage = errors[0]?.message || 'Failed to fetch messages';
      if (errorMessage.includes('FieldUndefined') || errorMessage.includes('getMessages')) {
        console.warn('⚠️ [getMessages] Backend query not implemented yet.');
        return { messages: [], nextToken: null };
      }
      console.error('❌ [getMessages] GraphQL errors:', errors);
      throw new Error(errorMessage);
    }

    const messages = data.getMessages?.messages || [];
    const resultNextToken = data.getMessages?.nextToken || null;

    console.log('✅ [getMessages] Success:', {
      messageCount: messages.length,
      hasNextToken: !!resultNextToken,
    });
    
    return {
      messages,
      nextToken: resultNextToken,
    };
  } catch (error) {
    const errorMessage = error.message || '';
    if (errorMessage.includes('FieldUndefined') || errorMessage.includes('getMessages')) {
      console.warn('⚠️ [getMessages] Backend not ready.');
      return { messages: [], nextToken: null };
    }
    console.error('❌ [getMessages] Unexpected error:', error);
    return { messages: [], nextToken: null };
  }
};

/**
 * Get Chat History from DynamoDB
 * First gets the latest conversation, then fetches its first page of messages
 * @param {string} userId - User ID
 * @param {number} limit - Number of messages to fetch
 * @returns {Promise<Object>} Object with items array, nextToken, conversationId, and hasHistory flag
 */
export const getAIChatHistory = async (userId, limit = 10, conversationId = null) => {
  try {
    console.log('🌐 [getAIChatHistory] Starting for userId:', userId, { conversationId });

    let resolvedConversationId = conversationId;

    if (!resolvedConversationId) {
      // Fallback: call listConversations only when no conversationId was supplied
      // (e.g. AI chat opened before the container's fetchAIUnreadCounts completed)
      const conversationsResult = await listConversations(userId, 1);

      if (!conversationsResult.conversations || conversationsResult.conversations.length === 0) {
        console.log('⚠️ [getAIChatHistory] No conversations found');
        return {
          items: [],
          nextToken: null,
          hasHistory: false,
        };
      }

      resolvedConversationId = conversationsResult.conversations[0].conversationId;
      console.log('📋 [getAIChatHistory] Resolved conversationId from API:', resolvedConversationId);
    } else {
      console.log('📋 [getAIChatHistory] Using supplied conversationId:', resolvedConversationId);
    }

    // Step 2: Get first page of messages for this conversation
    const messagesResult = await getMessages(resolvedConversationId, limit);
    
    console.log('✅ [getAIChatHistory] Success:', {
      conversationId: resolvedConversationId,
      messageCount: messagesResult.messages.length,
      hasNextToken: !!messagesResult.nextToken,
    });
    
    return {
      items: messagesResult.messages,
      nextToken: messagesResult.nextToken,
      conversationId: resolvedConversationId,
      hasHistory: messagesResult.messages.length > 0,
    };
  } catch (error) {
    const errorMessage = error.message || '';
    if (errorMessage.includes('FieldUndefined') || errorMessage.includes('listConversations') || errorMessage.includes('getMessages')) {
      console.warn('⚠️ [getAIChatHistory] Backend not ready. Starting new conversation.');
      return {
        items: [],
        nextToken: null,
        hasHistory: false,
      };
    }
    
    console.error('❌ [getAIChatHistory] Unexpected error:', error);
    return {
      items: [],
      nextToken: null,
      hasHistory: false,
    };
  }
};

/**
 * Fetch more messages for pagination (scroll to top)
 * @param {string} conversationId - Conversation ID
 * @param {string} nextToken - Pagination token from previous request
 * @param {number} limit - Number of messages to fetch 
 * @returns {Promise<Object>} Object with messages array and nextToken
 */
export const fetchMoreMessages = async (conversationId, nextToken, limit = 10) => {
  try {
    console.log('🌐 [fetchMoreMessages] Fetching more messages:', { conversationId, nextToken, limit });

    if (!nextToken) {
      console.log('⚠️ [fetchMoreMessages] No nextToken provided');
      return {
        messages: [],
        nextToken: null,
      };
    }

    const messagesResult = await getMessages(conversationId, limit, nextToken);
    
    console.log('✅ [fetchMoreMessages] Success:', {
      messageCount: messagesResult.messages.length,
      hasNextToken: !!messagesResult.nextToken,
    });
    
    return messagesResult;
  } catch (error) {
    console.error('❌ [fetchMoreMessages] Unexpected error:', error);
    return {
      messages: [],
      nextToken: null,
    };
  }
};

// GraphQL Query: Get Suggestions by Topic
const GET_SUGGESTIONS_BY_TOPIC_QUERY = gql`
  query GetSuggestionByTopic($domain: Domain!, $topic: String!, $locale: Locale!) {
    getSuggestionByTopic(domain: $domain, topic: $topic, locale: $locale) {
      id
      body
    }
  }
`;

/**
 * Fetch suggestions by topic
 * @param {Object} params - Query parameters
 * @param {string} params.domain - Domain enum: "ZONAR" or "GPST"
 * @param {string} params.topic - Topic string (e.g., "fleet")
 * @param {string} params.locale - Locale enum: "EN" or "ES"
 * @returns {Promise<Array>} Array of suggestions with id and body
 */
export const fetchSuggestionsByTopic = async ({ domain = 'GPST', topic = 'fleet', locale = 'EN' }) => {
  try {
    // Validate and sanitize inputs
    const validDomain = typeof domain === 'string' ? domain : 'GPST';
    const validTopic = typeof topic === 'string' ? topic : 'fleet';
    const validLocale = locale.toUpperCase() === 'ES' ? 'ES' : 'EN_US';
    
    console.log('🌐 [fetchSuggestionsByTopic] Fetching suggestions with validated params:', { 
      domain: validDomain, 
      topic: validTopic, 
      locale: validLocale,
      originalParams: { domain, topic, locale }
    });

    const { data, errors } = await appSyncClient.query({
      query: GET_SUGGESTIONS_BY_TOPIC_QUERY,
      variables: {
        domain: validDomain,
        topic: validTopic,
        locale: validLocale,
      },
      fetchPolicy: 'network-only', // Always fetch fresh suggestions
    });

    if (errors) {
      console.error('❌ [fetchSuggestionsByTopic] Query errors:', errors);
      throw new Error(errors[0]?.message || 'Failed to fetch suggestions');
    }

    const suggestions = data?.getSuggestionByTopic || [];
    console.log('✅ [fetchSuggestionsByTopic] Success:', { count: suggestions.length });
    
    return suggestions;
  } catch (error) {
    console.error('❌ [fetchSuggestionsByTopic] Unexpected error:', error);
    return [];
  }
};

/**
 * Get All Unread Counts for a user
 * Retrieves unread message counts for all conversations
 * @param {string} userId - User ID
 * @param {string} [conversationId] - Optional: filter to a specific conversation
 * @returns {Promise<Array>} Array of unread counts per conversation
 */
export const getAllUnreadCounts = async (userId, conversationId) => {
  try {
    console.log('🔔 [getAllUnreadCounts] Fetching unread counts for userId:', userId, { conversationId });

    const { data, errors } = await appSyncClient.query({
      query: GET_ALL_UNREAD_COUNTS_QUERY,
      variables: { userId, ...(conversationId && { conversationId }) },
      fetchPolicy: 'network-only', // Always fetch fresh data
    });

    if (errors) {
      const errorMessage = errors[0]?.message || 'Failed to fetch unread counts';
      if (errorMessage.includes('FieldUndefined') || errorMessage.includes('getAllUnreadCounts')) {
        console.warn('⚠️ [getAllUnreadCounts] Backend query not implemented yet.');
        return [];
      }
      console.error('❌ [getAllUnreadCounts] GraphQL errors:', errors);
      throw new Error(errorMessage);
    }

    const unreadCounts = data?.getAllUnreadCounts || [];
    console.log('✅ [getAllUnreadCounts] Success:', { count: unreadCounts.length, unreadCounts });
    
    return unreadCounts;
  } catch (error) {
    const errorMessage = error.message || '';
    if (errorMessage.includes('FieldUndefined') || errorMessage.includes('getAllUnreadCounts')) {
      console.warn('⚠️ [getAllUnreadCounts] Backend not ready.');
      return [];
    }
    console.error('❌ [getAllUnreadCounts] Unexpected error:', error);
    return [];
  }
};

/**
 * Mark Conversation as Read
 * Resets the unread count to 0 when user opens a conversation
 * @param {string} conversationId - Conversation ID
 * @param {string} lastMessageId - Last message ID that was read
 * @param {string} userId - User ID
 * @returns {Promise<Object>} Result object with success, unreadCount, lastReadAt
 */
export const markConversationAsRead = async (conversationId, lastMessageId, userId) => {
  try {
    console.log('📖 [markConversationAsRead] Marking conversation as read:', { 
      conversationId, 
      lastMessageId, 
      userId 
    });

    const { data, errors } = await appSyncClient.mutate({
      mutation: MARK_CONVERSATION_AS_READ_MUTATION,
      variables: {
        conversationId,
        lastMessageId,
        userId,
      },
    });

    if (errors) {
      const errorMessage = errors[0]?.message || 'Failed to mark conversation as read';
      if (errorMessage.includes('FieldUndefined') || errorMessage.includes('markConversationAsRead')) {
        console.warn('⚠️ [markConversationAsRead] Backend mutation not implemented yet.');
        return { success: false, error: 'Backend not ready' };
      }
      console.error('❌ [markConversationAsRead] GraphQL errors:', errors);
      throw new Error(errorMessage);
    }

    const result = data?.markConversationAsRead || {};
    console.log('✅ [markConversationAsRead] Success:', result);
    
    return result;
  } catch (error) {
    const errorMessage = error.message || '';
    if (errorMessage.includes('FieldUndefined') || errorMessage.includes('markConversationAsRead')) {
      console.warn('⚠️ [markConversationAsRead] Backend not ready.');
      return { success: false, error: 'Backend not ready' };
    }
    console.error('❌ [markConversationAsRead] Unexpected error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Cleanup subscription
 * @param {ZenObservable.Subscription} subscription
 */
export const cleanupSubscriptions = (subscription) => {
  if (subscription) {
    subscription.unsubscribe();
    console.log('✅ Subscription cleaned up');
  }
};
